import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Waves, Languages, LogOut, LayoutDashboard, Wallet as WalletIcon, Smartphone } from "lucide-react";

export const Header = () => {
  const { t, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const links = [
    { to: "/#modules", label: t("nav_modules") },
    { to: "/registry", label: t("nav_registry") },
    { to: "/marketplace", label: t("nav_marketplace") },
    { to: "/ledger", label: t("nav_ledger") },
    { to: "/justice", label: t("nav_justice") },
    { to: "/commons", label: t("nav_commons") },
    { to: "/tenders", label: t("nav_tenders") },
    { to: "/renewal", label: t("nav_renewal") },
    { to: "/edge", label: "Edge" },
    { to: "/social-housing", label: t("nav_social") },
  ];

  const onLogout = () => logout(undefined, () => nav("/"));

  return (
    <header className="qs-glass sticky top-0 z-50 w-full">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
          <span className="grid place-items-center h-9 w-9 rounded-sm bg-gradient-to-br from-[#8A2BE2] via-[#00FFFF] to-[#00FF7F]">
            <Waves className="h-5 w-5 text-[#050A15]" strokeWidth={2.5} />
          </span>
          <span className="font-display font-extrabold text-lg tracking-tight">Quantum<span className="qs-grad-text">Spectra</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-3 xl:gap-4 mx-4">
          {links.map((l) => (
            <a key={l.to} href={l.to} data-testid={`nav-${l.label}`}
               className="text-xs xl:text-[13px] whitespace-nowrap text-slate-300 hover:text-[#00FFFF] transition-colors">{l.label}</a>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0 whitespace-nowrap">
          <button data-testid="lang-toggle" onClick={toggle}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-mono uppercase text-slate-300 border border-white/10 hover:border-[#00FFFF]/50 hover:text-[#00FFFF] transition-colors">
            <Languages className="h-3.5 w-3.5" /> {lang}
          </button>
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-200 hover:text-[#00FFFF] transition-colors">
                <LayoutDashboard className="h-4 w-4" /> {t("nav_dashboard")}
              </Link>
              <Link to="/wallet" data-testid="nav-wallet"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-200 hover:text-[#00FF7F] transition-colors">
                <WalletIcon className="h-4 w-4" /> {t("nav_wallet")}
              </Link>
              <Link to="/field" data-testid="nav-field" title="Field capture"
                className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-slate-200 hover:text-[#F472B6] transition-colors">
                <Smartphone className="h-4 w-4" />
              </Link>
              <button data-testid="logout-btn" onClick={onLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-[#EF4444] transition-colors">
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login"
                className="px-3 py-1.5 text-sm text-slate-200 hover:text-[#00FFFF] transition-colors">{t("nav_login")}</Link>
              <Link to="/register" data-testid="nav-signup"
                className="qs-cta px-4 py-2 text-sm font-semibold bg-[#00FFFF] text-[#050A15] hover:bg-[#00FF7F]">{t("nav_signup")}</Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
};
